import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useLanguage } from "../context/LanguageContext";
import PublicNav from "../components/PublicNav";
import LegalEntityDisclosure from "../components/LegalEntityDisclosure";
import api from "../lib/api";
import { SUPPORT_EMAIL } from "../lib/brand";
import { toast } from "sonner";
import { 
  ArrowLeft,
  Send,
  Phone,
  Mail,
  Clock,
  CheckCircle2,
  Calendar,
  MessageCircle
} from "lucide-react";

const ContactPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "general",
    message: "",
    preferred_contact: "email",
    investment_range: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await api.post("/leads/contact", formData);
      setSubmitted(true);
      toast.success("Message sent! We'll be in touch shortly.");
    } catch (error) {
      // The POST failed — the lead was NOT stored. Surface the failure honestly:
      // do not flip to the success screen, keep the form populated for retry, and
      // log status + server detail only (never the full Axios error — its
      // config.data carries the submitted form PII). The `finally` re-enables
      // the submit button so the user can resend immediately.
      const serverMessage = error?.response?.data?.detail;
      console.error(
        "ContactPage: lead submission failed",
        { status: error?.response?.status ?? null, detail: serverMessage ?? error?.message ?? null }
      );
      if (error?.response) {
        // Backend rejected the request (non-2xx) — surface the server's message if any.
        toast.error(
          (typeof serverMessage === "string" && serverMessage) ||
            `We couldn't send your message. Please try again, or email us directly at ${SUPPORT_EMAIL}.`
        );
      } else {
        // No response — network failure (offline, DNS, timeout).
        toast.error(
          `We couldn't reach our servers. Please check your connection and try again, or email us directly at ${SUPPORT_EMAIL}.`
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-8">
        <Card className="bg-surface border-platinum/15 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="text-emerald" size={32} />
            </div>
            <h2 className="text-2xl font-serif text-slate-900 mb-4">Message Received!</h2>
            <p className="text-platinum/70 mb-6">
              Thank you for reaching out. A member of our team will contact you within 24 hours.
            </p>
            <div className="space-y-3">
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                onClick={() => navigate("/consultation")}
              >
                <Calendar className="mr-2" size={18} />
                Schedule a Call Now
              </Button>
              <Button 
                variant="outline"
                className="w-full border-platinum/30 text-platinum"
                onClick={() => navigate("/")}
              >
                Return Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <PublicNav active="contact" />

      <div className="py-16 px-8">
        <div className="max-w-6xl mx-auto">
          <Button 
            variant="ghost" 
            className="text-platinum/60 hover:text-platinum mb-6"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} className="mr-2" />
            Back
          </Button>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <p className="text-burgundy uppercase tracking-[0.3em] text-sm mb-4">Get in Touch</p>
              <h1 className="text-4xl md:text-5xl font-semibold text-slate-900 font-serif leading-tight mb-6">
                We're Here to <span className="gradient-platinum">Help</span>
              </h1>
              <p className="text-platinum/70 text-lg mb-8">
                Have questions about the verification portal, the introducer network, or the
                regulated partner onboarding pathway? We'd love to hear from you.
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-sm bg-burgundy/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-burgundy" size={20} />
                  </div>
                  <div>
                    <h3 className="text-cream font-medium">{language === 'en' ? 'Email' : 'Correo'}</h3>
                    <a href="mailto:admin@echosensecapital.com" className="text-platinum/60 hover:text-platinum transition-colors">
                      admin@echosensecapital.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-sm bg-burgundy/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="text-burgundy" size={20} />
                  </div>
                  <div>
                    <h3 className="text-cream font-medium">{language === 'en' ? 'Phone' : 'Teléfono'}</h3>
                    <a href="tel:+17025180208" className="block text-platinum/60 hover:text-platinum transition-colors">
                      (702) 518-0208
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-sm bg-burgundy/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="text-burgundy" size={20} />
                  </div>
                  <div>
                    <h3 className="text-cream font-medium">{language === 'en' ? 'Response Time' : 'Tiempo de Respuesta'}</h3>
                    <p className="text-platinum/60">{language === 'en' ? 'Within 24 hours' : 'Dentro de 24 horas'}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 rounded-lg bg-surface border border-platinum/15">
                <h3 className="text-cream font-medium mb-3 flex items-center gap-2">
                  <Calendar className="text-burgundy" size={18} />
                  Prefer to Talk?
                </h3>
                <p className="text-platinum/60 text-sm mb-4">
                  Skip the form and schedule a call directly with our team.
                </p>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                  onClick={() => navigate("/consultation")}
                >
                  Schedule a Free Consultation
                </Button>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="bg-surface border-platinum/15">
              <CardContent className="p-8">
                <h2 className="text-xl font-serif text-slate-900 mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-platinum/80">Full Name *</Label>
                      <Input
                        required
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="bg-white border-slate-200 text-slate-900"
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-platinum/80">Email *</Label>
                      <Input
                        required
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="bg-white border-slate-200 text-slate-900"
                        placeholder="john@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-platinum/80">Phone</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="bg-white border-slate-200 text-slate-900"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-platinum/80">Investment Range</Label>
                      <Select 
                        value={formData.investment_range} 
                        onValueChange={(v) => setFormData(prev => ({ ...prev, investment_range: v }))}
                      >
                        <SelectTrigger className="bg-surface border-platinum/20 text-platinum">
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                        <SelectContent className="bg-surface border-platinum/20">
                          <SelectItem value="25k-100k">$25,000 - $100,000</SelectItem>
                          <SelectItem value="100k-500k">$100,000 - $500,000</SelectItem>
                          <SelectItem value="500k-1m">$500,000 - $1,000,000</SelectItem>
                          <SelectItem value="1m+">$1,000,000+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-platinum/80">Subject *</Label>
                    <Select 
                      value={formData.subject} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, subject: v }))}
                    >
                      <SelectTrigger className="bg-surface border-platinum/20 text-platinum">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-surface border-platinum/20">
                        <SelectItem value="general">General Inquiry</SelectItem>
                        <SelectItem value="investment">Investment Opportunities</SelectItem>
                        <SelectItem value="partnership">Partnership/Referral Program</SelectItem>
                        <SelectItem value="support">Account Support</SelectItem>
                        <SelectItem value="callback">Request Callback</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-platinum/80">Message *</Label>
                    <Textarea
                      required
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      className="bg-white border-slate-200 text-slate-900 min-h-[120px]"
                      placeholder="How can we help you?"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-platinum/80">Preferred Contact Method</Label>
                    <Select 
                      value={formData.preferred_contact} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, preferred_contact: v }))}
                    >
                      <SelectTrigger className="bg-surface border-platinum/20 text-platinum">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-surface border-platinum/20">
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="either">Either</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                  >
                    <Send size={18} className="mr-2" />
                    {submitting ? "Sending..." : "Send Message"}
                  </Button>

                  <p className="text-platinum/40 text-xs text-center">
                    We respect your privacy. Your information will never be shared.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-8 bg-obsidian border-t border-platinum/10">
        <div className="max-w-7xl mx-auto text-center space-y-3">
          <LegalEntityDisclosure variant="footer" />
          <p className="text-platinum/40 text-xs">
            © {new Date().getFullYear()} EchoSense Capital Club. All rights reserved.{' · '}<Link to="/disclaimer" className="text-platinum/40 hover:text-platinum text-xs underline-offset-2 hover:underline" data-testid="footer-disclaimer">{language === 'en' ? 'Disclaimer' : 'Aviso legal'}</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
